import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessFormModal from "../../Business/BusinessFormModal";
import {
  PlusIcon,
  BookOpenIcon,
  CashIcon,
  ChartBarIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";

const MockupRows = () => {
  const rows = [
    {
      date: "12 Feb 2026",
      note: "Daily product sales",
      type: "Income",
      typeColor: "bg-green-100 text-green-800",
      amount: "+ Rp 3.500.000",
      amountColor: "text-green-600",
    },
    {
      date: "11 Feb 2026",
      note: "Purchase of raw materials",
      type: "Expense",
      typeColor: "bg-red-100 text-red-800",
      amount: "- Rp 1.200.000",
      amountColor: "text-red-600",
    },
    {
      date: "10 Feb 2026",
      note: "Operational expenses",
      type: "Expense",
      typeColor: "bg-red-100 text-red-800",
      amount: "- Rp 450.000",
      amountColor: "text-red-600",
    },
    {
      date: "09 Feb 2026",
      note: "Service revenue",
      type: "Income",
      typeColor: "bg-green-100 text-green-800",
      amount: "+ Rp 2.000.000",
      amountColor: "text-green-600",
    },
  ];

  return (
    <div className="mt-6">
      <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Note
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.date}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.note}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${row.typeColor}`}
                  >
                    {row.type}
                  </span>
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${row.amountColor}`}
                >
                  {row.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FeatureItem = ({ icon: Icon, text }) => (
  <div className="flex items-center space-x-3">
    <div className="flex-shrink-0">
      <Icon className="h-5 w-5 text-blue-600" aria-hidden="true" />
    </div>
    <p className="text-sm text-gray-600">{text}</p>
  </div>
);

const CashbookEmptyOverlay = () => {
  const { t } = useTranslation();
  const [createBusinessOpen, setCreateBusinessOpen] = useState(false);

  return (
    <>
      <main className="lg:col-span-9">
        {/* Page header - mirrors real cashbook header */}
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {t("menu.cashbook")}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
              <div className="inline-flex rounded-md shadow-sm w-full sm:w-auto opacity-50 pointer-events-none">
                <button
                  type="button"
                  className="relative inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white flex-1 sm:flex-none"
                  disabled
                >
                  <span className="mr-2">
                    <PlusIcon className="h-5 w-5" />
                  </span>
                  {t("cashbook.header.title.add")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content area with mockup + overlay */}
        <div className="relative min-h-[520px]">
          <div
            className="px-4 sm:px-6 py-6 select-none"
            aria-hidden="true"
            style={{ filter: "blur(3px)", opacity: 0.5, pointerEvents: "none" }}
          >
            <MockupRows />
          </div>

          {/* Overlay CTA */}
          <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[2px]">
            <div className="mx-4 max-w-md w-full">
              <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden">
                <div className="px-6 py-8 sm:px-10">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <OfficeBuildingIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>

                  <div className="text-center mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 tracking-tight mb-2">
                      {t("cashbook.emptyOverlay.title")}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t("cashbook.emptyOverlay.description")}
                    </p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 text-center">
                      {t("cashbook.emptyOverlay.unlockFeatures")}
                    </p>
                    <FeatureItem
                      icon={BookOpenIcon}
                      text={t(
                        "cashbook.emptyOverlay.feature.trackTransactions",
                      )}
                    />
                    <FeatureItem
                      icon={CashIcon}
                      text={t("cashbook.emptyOverlay.feature.monitorBalance")}
                    />
                    <FeatureItem
                      icon={ChartBarIcon}
                      text={t("cashbook.emptyOverlay.feature.viewReports")}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => setCreateBusinessOpen(true)}
                    className="w-full inline-flex items-center justify-center px-4 h-10 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    {t("cashbook.emptyOverlay.createBusiness")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <BusinessFormModal
        open={createBusinessOpen}
        onClose={() => setCreateBusinessOpen(false)}
        onboarding
      />
    </>
  );
};

export default CashbookEmptyOverlay;
